import React, { useState, FormEvent, useRef, useEffect } from "react";
import ActionModal from "./ActionModal";
import AdminCounter from "./AdminCounter";
import type { AdminAction } from "../types/admin";
import { MAX_ALLOWED_SPACESHIP_DAMAGE } from "../shared/constants";

interface ActionReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  action: AdminAction | null;
  onSave: (scanResult: string) => Promise<void>;
  isSubmitting?: boolean;
  spaceshipCurrentLocation?: string | null;
  spaceshipDamage?: number | null;
  spaceshipShieldsOn?: boolean | null;
}

export default function ActionReviewModal({ isOpen, onClose, action, onSave, isSubmitting = false, spaceshipCurrentLocation, spaceshipDamage, spaceshipShieldsOn }: ActionReviewModalProps) {
  const [scanResult, setScanResult] = useState<string>("");
  const [movePath, setMovePath] = useState<string>("");
  const [newCurrentLocation, setNewCurrentLocation] = useState<string>("");
  const [damage, setDamage] = useState<number>(spaceshipDamage ?? 0);
  const [shieldsOn, setShieldsOn] = useState<boolean>(spaceshipShieldsOn ?? false);
  const [error, setError] = useState<string>("");
  const scanResultInputRef = useRef<HTMLInputElement>(null);
  const movePathInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setScanResult("");
      setMovePath("");
      setNewCurrentLocation("");
      setError("");
      // Update damage and shields from props when modal opens
      if (spaceshipDamage !== undefined && spaceshipDamage !== null) {
        setDamage(spaceshipDamage);
      }
      if (spaceshipShieldsOn !== undefined && spaceshipShieldsOn !== null) {
        setShieldsOn(spaceshipShieldsOn);
      }
      // Auto-focus the appropriate input when the modal opens
      setTimeout(() => {
        const isSpaceshipMove = action && (action.action === "Move" || action.action === "MOVE") && 
          action.player_role && action.player_role.toLowerCase().includes("spaceship");
        if (isSpaceshipMove && movePathInputRef.current) {
          movePathInputRef.current.focus();
        } else if (scanResultInputRef.current) {
          scanResultInputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen, action, spaceshipDamage, spaceshipShieldsOn]);

  if (!action) {
    return null;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const isSpaceshipMove = action && (action.action === "Move" || action.action === "MOVE") && 
      action.player_role && action.player_role.toLowerCase().includes("spaceship");
    
    if (isSpaceshipMove) {
      if (!movePath.trim() || !newCurrentLocation.trim()) {
        setError("Path and New Current location are required");
        return;
      }
    } else {
      if (!scanResult.trim()) {
        setError("Scan result is required");
        return;
      }
    }
    
    if (isSubmitting) {
      return; // Prevent double submission
    }
    setError("");
    // Don't close modal here - let handleReviewSave handle it after message is sent
    if (isSpaceshipMove) {
      await onSave(JSON.stringify({ 
        path: movePath.trim(), 
        newCurrentLocation: newCurrentLocation.trim(),
        damage: damage,
        shieldsOn: shieldsOn
      }));
    } else {
      await onSave(scanResult.trim());
    }
  };

  const handleCancel = () => {
    setScanResult("");
    setMovePath("");
    setNewCurrentLocation("");
    setError("");
    // Reset damage and shields to original values
    if (spaceshipDamage !== undefined && spaceshipDamage !== null) {
      setDamage(spaceshipDamage);
    }
    if (spaceshipShieldsOn !== undefined && spaceshipShieldsOn !== null) {
      setShieldsOn(spaceshipShieldsOn);
    }
    onClose();
  };

  const handleDamageChange = async (next: number) => {
    const bounded = Math.max(0, Math.min(MAX_ALLOWED_SPACESHIP_DAMAGE, next));
    setDamage(bounded);
  };

  const handleShieldsToggle = (nextValue: boolean) => {
    setShieldsOn(nextValue);
  };

  // Check if this is a Scan action by a spaceship
  const isSpaceshipScan = (action.action === "Scan" || action.action === "SCAN") && 
    action.player_role && action.player_role.toLowerCase().includes("spaceship");

  // Check if this is a Scan action by an agency
  const isAgencyScan = (action.action === "Scan" || action.action === "SCAN") && 
    action.player_role && action.player_role.toLowerCase().includes("agency");

  // Check if this is a Move action by a spaceship
  const isSpaceshipMove = (action.action === "Move" || action.action === "MOVE") && 
    action.player_role && action.player_role.toLowerCase().includes("spaceship");

  // For Scan by spaceship or agency
  const details = action.details || "";
  const scanLevel = action.scanning_agency_scan_lvl ?? null;

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={`Action Review - ${action.action}`}
    >
      {(isSpaceshipScan || isAgencyScan) ? (
        <form onSubmit={handleSubmit} style={{ padding: "1rem 0" }}>
          <div style={{ marginBottom: "1rem" }}>
            <label 
              style={{ 
                display: "block", 
                marginBottom: "0.5rem", 
                fontWeight: "bold", 
                color: "#212529" 
              }}
            >
              <span style={{ whiteSpace: "pre-line" }}>{details}</span>
              {isAgencyScan && scanLevel !== null && ` (Scan Level ${scanLevel})`}:
            </label>
            <input
              type="text"
              value={scanResult}
              onChange={(e) => {
                setScanResult(e.target.value);
                setError("");
              }}
              style={{
                width: "100%",
                padding: "0.5rem",
                fontSize: "1rem",
                border: error ? "1px solid #dc3545" : "1px solid #dee2e6",
                borderRadius: "4px",
              }}
              placeholder="Enter scan result"
              ref={scanResultInputRef}
            />
            {error && (
              <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.5rem" }}>
                {error}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                padding: "0.5rem 1rem",
                cursor: "pointer",
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!scanResult.trim() || isSubmitting}
              style={{
                padding: "0.5rem 1rem",
                cursor: (scanResult.trim() && !isSubmitting) ? "pointer" : "not-allowed",
                backgroundColor: (scanResult.trim() && !isSubmitting) ? "#007bff" : "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                opacity: (scanResult.trim() && !isSubmitting) ? 1 : 0.6,
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
          {spaceshipCurrentLocation && (
            <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid #dee2e6", fontSize: "0.875rem", color: "#6c757d" }}>
              Spaceship Location: <strong>{spaceshipCurrentLocation}</strong>
            </div>
          )}
        </form>
      ) : isSpaceshipMove ? (
        <form onSubmit={handleSubmit} style={{ padding: "1rem 0" }}>
          <div style={{ marginBottom: "1.5rem", fontWeight: "bold", color: "#212529", fontSize: "1rem" }}>
            <span style={{ whiteSpace: "pre-line" }}>{details}</span>:
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <label 
              style={{ 
                display: "block", 
                marginBottom: "0.5rem", 
                fontWeight: "bold", 
                color: "#212529" 
              }}
            >
              Path:
            </label>
            <input
              type="text"
              value={movePath}
              onChange={(e) => {
                setMovePath(e.target.value);
                setError("");
              }}
              style={{
                width: "100%",
                padding: "0.5rem",
                fontSize: "1rem",
                border: error ? "1px solid #dc3545" : "1px solid #dee2e6",
                borderRadius: "4px",
              }}
              placeholder="Enter path"
              ref={movePathInputRef}
            />
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <label 
              style={{ 
                display: "block", 
                marginBottom: "0.5rem", 
                fontWeight: "bold", 
                color: "#212529" 
              }}
            >
              New Current location:
            </label>
            <input
              type="text"
              value={newCurrentLocation}
              onChange={(e) => {
                setNewCurrentLocation(e.target.value);
                setError("");
              }}
              style={{
                width: "100%",
                padding: "0.5rem",
                fontSize: "1rem",
                border: error ? "1px solid #dc3545" : "1px solid #dee2e6",
                borderRadius: "4px",
              }}
              placeholder="Enter new current location"
            />
            {error && (
              <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.5rem" }}>
                {error}
              </div>
            )}
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <AdminCounter
              label="Damage"
              value={damage}
              min={0}
              max={MAX_ALLOWED_SPACESHIP_DAMAGE}
              disabled={isSubmitting}
              onChangeRequested={handleDamageChange}
            />
          </div>
          <div style={{ marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <strong>Shields:</strong>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "0.25rem", cursor: isSubmitting ? "not-allowed" : "pointer" }}>
                <input
                  type="radio"
                  name={`shields-${action?.player_id || 'modal'}`}
                  checked={shieldsOn === true}
                  onChange={() => handleShieldsToggle(true)}
                  disabled={isSubmitting}
                />
                On
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "0.25rem", cursor: isSubmitting ? "not-allowed" : "pointer" }}>
                <input
                  type="radio"
                  name={`shields-${action?.player_id || 'modal'}`}
                  checked={shieldsOn === false}
                  onChange={() => handleShieldsToggle(false)}
                  disabled={isSubmitting}
                />
                Off
              </label>
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                padding: "0.5rem 1rem",
                cursor: "pointer",
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!movePath.trim() || !newCurrentLocation.trim() || isSubmitting}
              style={{
                padding: "0.5rem 1rem",
                cursor: (movePath.trim() && newCurrentLocation.trim() && !isSubmitting) ? "pointer" : "not-allowed",
                backgroundColor: (movePath.trim() && newCurrentLocation.trim() && !isSubmitting) ? "#007bff" : "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                opacity: (movePath.trim() && newCurrentLocation.trim() && !isSubmitting) ? 1 : 0.6,
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      ) : (
        // For other action types, show a simple confirmation
        <div style={{ padding: "1rem 0" }}>
          <div style={{ marginBottom: "1rem", color: "#212529" }}>
            Confirm status change for this action?
          </div>
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                padding: "0.5rem 1rem",
                cursor: "pointer",
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={async () => {
                if (isSubmitting) return;
                await onSave("");
              }}
              disabled={isSubmitting}
              style={{
                padding: "0.5rem 1rem",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                backgroundColor: isSubmitting ? "#6c757d" : "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                opacity: isSubmitting ? 0.6 : 1,
              }}
            >
              {isSubmitting ? "Submitting..." : "Confirm"}
            </button>
          </div>
        </div>
      )}
    </ActionModal>
  );
}

